import {LiveAnnouncer} from '@angular/cdk/a11y';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {LoaderService} from '@core/src/public-api';
import {appointmentLinkVerificationCodes} from '@shared/constants/appointment.constants';
import {configVariableValues} from '@shared/constants/config-variable-values.constants';
import {Session} from '@shared/models/session.model';
import {UserProfileLinkVerification} from '@shared/models/user-profile.model';
import {AppConfigService} from '@shared/services/app-config.service';
import {LinkPatientService} from '@shared/services/link-patient.service';
import {LoginService} from '@shared/services/login.service';
import {ProfileService} from '@shared/services/profile.service';

@Component({
  selector: 'si-verification-parent',
  templateUrl: './verification-parent.component.html',
  styleUrls: ['./verification-parent.component.scss']
})
export class VerificationParentComponent {
  @Input() reloadImmediately: boolean;
  @Input() refreshUserRole: boolean;
  @Output() refreshUserRoleChange = new EventEmitter<boolean>();
  @Output() linkPatientSuccessChange = new EventEmitter<any>();
  model = new UserProfileLinkVerification();
  linkPatientErrorText: string;
  verificationErrorText: string;
  displayLinkPatientErrorDialog: boolean;
  displayLinkPatientSuccessDialog: boolean;
  displayVerificationDialog: boolean;
  hasSmsVerification: boolean;
  hasEmailVerification: boolean;
  private _linkPatientVerification: number;

  constructor(
    private profileService: ProfileService,
    private loginService: LoginService,
    private loaderService: LoaderService,
    private liveAnnouncer: LiveAnnouncer,
    private linkPatientService: LinkPatientService,
    private appConfigService: AppConfigService
  ) {
  }

  get linkPatientVerification(): number {
    return this._linkPatientVerification;
  }

  @Input()
  set linkPatientVerification(val) {
    this._linkPatientVerification = val;
    if (val) {
      this.updateLinkPatient();
    }
  }

  ngOnInit(): void {
    this.hasSmsVerification = this.appConfigService.getVariableValue(configVariableValues.DOUBLE_FACTOR_AUTH_METHOD) === appointmentLinkVerificationCodes.SMS;
    this.hasEmailVerification = this.appConfigService.getVariableValue(configVariableValues.DOUBLE_FACTOR_AUTH_METHOD) === appointmentLinkVerificationCodes.EMAIL;
  }

  async updateLinkPatient(): Promise<void> {
    if (!this.model.userProfile) {
      await this.getUserProfile();
    }

    this.loaderService.start();
    try {
      const patientSession = await this.loginService.updateMatchExistingPatient(this.model);
      this.successLinkPatient(patientSession);
    } catch (e) {
      if (e.error?.applicationCode === 'PCS0008') {
        this.linkPatientErrorText = e.error?.message;
        this.displayLinkPatientErrorDialog = true;
      }

      if (e.error?.applicationCode === 'PCS0034') {
        this.displayVerificationDialog = true;
      }

      if (e.error?.applicationCode === 'PCS0035') {
        this.displayVerificationDialog = false;
        this.onChangeDisplayVerificationDialog(false);
      }

      if (e.error?.applicationCode === 'PCS0036') {
        this.verificationErrorText = e.error?.message;
        this.liveAnnouncer.announce(e.error.message, 'assertive');
      }

      throw e;
    } finally {
      this.loaderService.stop();
    }
  }

  async getUserProfile(): Promise<void> {
    this.loaderService.start();
    try {
      this.model.userProfile = await this.profileService.getUserProfile();
    } finally {
      this.loaderService.stop();
    }
  }

  successLinkPatient(patientSession: Session): void {
    this.refreshUserRole = true;
    this.refreshUserRoleChange.emit(this.refreshUserRole);
    this.loginService.afterLogin(patientSession);
    this.linkPatientService.setShowLinkPatientWarning(false);
    this.displayVerificationDialog = false;
    this.displayLinkPatientSuccessDialog = true;
  }

  onChangeDisplayVerificationDialog(displayDialog: boolean): void {
    if (!displayDialog && !this.refreshUserRole) {
      this.model.verificationCode = null;
      this.verificationErrorText = null;
    }
  }

  onChangeVerificationCode(): void {
    this.updateLinkPatient();
  }

  onChangeLinkPatientSuccessDialog(displayDialog: boolean): void {
    if (displayDialog) {
      return;
    }

    this.linkPatientSuccessChange.emit();
    if (this.reloadImmediately) {
      window.location.href = '/';
    }
  }
}
